import { useTriggerLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoFileUpload } from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { parseCsvValues } from "../../../utils/parse-csv-values";

interface UploadCsvModalProps {
  open: boolean;
  onClose: () => void;
  onValuesUploaded: (values: string[]) => void;
  possibleColumnNames?: string[];
  valueLabel?: string;
}

export const UploadCsvModal = memo(function UploadCsvModal({
  open,
  onClose,
  onValuesUploaded,
  possibleColumnNames,
  valueLabel,
}: UploadCsvModalProps) {
  const { t } = useTranslation();
  const displayValueLabel = valueLabel ?? t("values");
  const [file, setFile] = useState<File | null>(null);

  const handleFilesAdded = (files: FileList | null) => {
    if (files && files.length > 0) {
      const firstFile = files[0];
      if (firstFile) {
        setFile(firstFile);
      }
    }
  };

  const handleFileDeleted = () => {
    setFile(null);
  };

  const [uploadLoad, doUpload] = useTriggerLoad((signal) =>
    alertOnFailure(
      t("Error uploading {{valueLabel}}", { valueLabel: displayValueLabel }),
    )(async () => {
      if (!file) {
        return;
      }

      const values = await parseCsvValues(file, possibleColumnNames);

      if (values.length === 0) {
        toast(
          t("No {{valueLabel}} found in the CSV file", {
            valueLabel: displayValueLabel,
          }),
          { variant: "warning" },
        );
        return;
      }

      toast(
        t("Successfully uploaded {{count}} {{valueLabel}}", {
          count: values.length,
          valueLabel: displayValueLabel,
        }),
        { variant: "success" },
      );

      onValuesUploaded(values);
      onClose();
      setFile(null);
    }),
  );

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  const columnNames = possibleColumnNames?.join(", ");

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: t("Upload"),
        onClick: doUpload,
        size: "sm",
        pending: uploadLoad.pending,
        disabled: !file,
      }}
      secondaryButton={{
        text: t("Cancel"),
        onClick: handleClose,
        size: "sm",
        disabled: uploadLoad.pending,
      }}
      title={t("Upload CSV")}
    >
      <Flex dir="column" gap="2xl" w="full">
        <RedoFileUpload
          accept="text/csv"
          acceptMessage={t("CSV")}
          files={file ? [file] : []}
          fullWidth
          handleFileDeleted={handleFileDeleted}
          handleFilesAdded={handleFilesAdded}
          nFilesToAccept={1}
        />
        <Text fontSize="sm" textColor="secondary">
          {columnNames
            ? t(
                "Upload a CSV file with {{valueLabel}}. The file should include a column named one of: {{columnNames}}. If no matching column is found, the first column will be used. Only values in the first column will be used.",
                { valueLabel: displayValueLabel, columnNames },
              )
            : t(
                "Upload a CSV file with {{valueLabel}}. The file should include a column with any column name. If no matching column is found, the first column will be used. Only values in the first column will be used.",
                { valueLabel: displayValueLabel },
              )}
        </Text>
      </Flex>
    </RedoModal>
  );
});
